import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { HoodieSVG, TeeSVG, CrewneckSVG, CapSVG } from "./Garments";

const EASE = [0.16, 1, 0.3, 1];

const GARMENTS = { hoodie: HoodieSVG, tee: TeeSVG, crewneck: CrewneckSVG, cap: CapSVG };

const DEFAULT_PRODUCTS = [
    { id: "p1", number: "001", name: "Signal Hoodie", detail: "480gsm loopback fleece", price: "€240", garment: "hoodie" },
    { id: "p2", number: "002", name: "Static Tee", detail: "Boxy fit, garment-dyed", price: "€95", garment: "tee" },
    { id: "p3", number: "003", name: "Ghost Crewneck", detail: "Brushed back fleece", price: "€180", garment: "crewneck" },
    { id: "p4", number: "004", name: "Deadzone Cap", detail: "6-panel, embroidered mark", price: "€75", garment: "cap" },
];

const CLIPS = [
    "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
    "polygon(4% 0, 100% 0, 100% 96%, 0 100%)",
    "polygon(0 4%, 96% 0, 100% 100%, 0 100%)",
    "polygon(0 0, 100% 3%, 96% 100%, 0 97%)",
];

const TiltCard = ({ product, index }) => {
    const ref = useRef(null);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rotateX = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 120, damping: 16 });
    const rotateY = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 120, damping: 16 });

    const onMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
    };
    const onLeave = () => { mx.set(0.5); my.set(0.5); };

    const G = GARMENTS[product.garment] || HoodieSVG;
    const num = product.number;
    const clip = CLIPS[index % CLIPS.length];
    return (
        <motion.div
            initial={{ y: 80, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: EASE, delay: (index % 2) * 0.12 }}
            className={index % 2 === 1 ? "md:mt-24" : ""}
            data-testid={`product-card-${num}`}
        >
            <motion.div
                ref={ref}
                onMouseMove={onMove}
                onMouseLeave={onLeave}
                style={{ rotateX, rotateY, transformPerspective: 900, clipPath: clip }}
                className="group relative border border-zinc-800 bg-[#0A0A0A] aspect-[4/5] flex items-center justify-center overflow-hidden transition-colors duration-500 hover:border-zinc-600"
            >
                <div className="spotlight absolute inset-0 transition-opacity duration-700 opacity-60 group-hover:opacity-100" />
                {product.image_url ? (
                    <img src={product.image_url} alt={product.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 brightness-90 transition-transform duration-700 group-hover:scale-105" />
                ) : (
                    <G className="relative z-10 w-3/5 max-w-[260px] float-slow transition-transform duration-700 group-hover:scale-105" />
                )}
                <span className="absolute top-4 left-4 font-display text-sm tracking-widest text-zinc-600">
                    {num}
                </span>
                <span className="absolute top-4 right-4 text-[10px] tracking-[0.25em] uppercase text-zinc-600 group-hover:text-signal transition-colors duration-500">
                    Void Series
                </span>
            </motion.div>
            <div className="flex items-end justify-between mt-5 px-1">
                <div>
                    <h3 className="font-display font-medium uppercase tracking-wide text-lg sm:text-xl" data-testid={`product-name-${num}`}>
                        {product.name}
                    </h3>
                    <p className="text-xs tracking-[0.15em] uppercase text-zinc-500 mt-1">{product.detail}</p>
                </div>
                <div className="text-right">
                    <p className="font-display text-lg sm:text-xl text-zinc-200" data-testid={`product-price-${num}`}>{product.price}</p>
                    <button
                        data-testid={`product-notify-${num}`}
                        onClick={() => document.querySelector("#drop")?.scrollIntoView({ behavior: "smooth" })}
                        className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-signal transition-colors duration-300 mt-1"
                    >
                        Notify me →
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export const Collection = ({ products = DEFAULT_PRODUCTS, blurb = "Four artifacts. One mark. Rendered here as studio plates — the physical drop ships 10.08.2026 to the blacklist first.", title = "The Garments" }) => (
    <section id="collection" className="relative px-6 sm:px-12 py-32 sm:py-44 border-t border-zinc-900" data-testid="collection-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-20 sm:mb-28">
            <motion.h2
                initial={{ y: 60, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease: EASE }}
                className="font-display font-semibold uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl"
            >
                {title.split(" ")[0]}<br />{title.split(" ").slice(1).join(" ")}
            </motion.h2>
            <motion.p
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease: EASE, delay: 0.15 }}
                className="text-sm leading-relaxed text-zinc-400 max-w-xs"
            >
                {blurb}
            </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-20 max-w-6xl mx-auto">
            {products.map((p, i) => (
                <TiltCard key={p.id || p.number} product={p} index={i} />
            ))}
        </div>
    </section>
);
